import React, { useMemo, useState } from 'react';
import Select, { components } from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentStrategy,
  getStrategyTactics,
  isStrategyLoading,
  getFormationStyles,
  getCurrentMatchRunning,
} from '../../../../../../redux/selector/Team';
import { getMyCurrentTeam } from '../../../../../../redux/selector/User';
import { teamPickUpStrategy, setTacticData, setUnsavedChanges, setMindsetData, setEffortData } from '../../../../../../redux/actions/Team';
import { STRATEGY_NAMES } from '../../../../../../service/constants';
import { Toolbar, Control } from '../../layout';
import {
  formationStyleOf,
  matchupLabel,
  ownClubId,
  EFFORT_LABELS,
  EFFORT_EXPLAINERS,
  EFFORT_FOOTNOTE,
} from '../../../../../../service/matchEngine';
import { ReactComponent as F442 } from '../../../../../../assets/img/transfers-tactics/4-4-2.svg';
import { ReactComponent as F433 } from '../../../../../../assets/img/transfers-tactics/4-3-3.svg';
import { ReactComponent as F4231 } from '../../../../../../assets/img/transfers-tactics/4-2-3-1.svg';
import { ReactComponent as F451 } from '../../../../../../assets/img/transfers-tactics/4-5-1.svg';
import { ReactComponent as F352 } from '../../../../../../assets/img/transfers-tactics/3-5-2.svg';
import { ReactComponent as F4411 } from '../../../../../../assets/img/transfers-tactics/4-4-1-1.svg';
import { ReactComponent as Shield } from '../../../../../../assets/icons/lucide/shield.svg';
import { ReactComponent as Scale } from '../../../../../../assets/icons/lucide/scale.svg';
import { ReactComponent as Swords } from '../../../../../../assets/icons/lucide/swords.svg';
import { ReactComponent as Gauge } from '../../../../../../assets/icons/lucide/gauge.svg';
import { ReactComponent as Flame } from '../../../../../../assets/icons/lucide/flame.svg';
import { ReactComponent as Zap } from '../../../../../../assets/icons/lucide/zap.svg';

const formations = {'4-4-2':F442,'4-3-3':F433,'4-2-3-1':F4231,'4-5-1':F451,'3-5-2':F352,'4-4-1-1':F4411};
const tacticIcons = [Shield, Scale, Swords];
const effortIcons = [Gauge, Flame, Zap];
const STYLE_FILTERS = ['Wide', 'Central', 'Direct'];

function StrategySelect({label, value, options, onChange, disabled, className, title}) {
  const defaultTitle =
    label === 'Mindset'
      ? 'An attacking team puts more effort toward scoring and less toward defending.'
      : label === 'Effort'
        ? [
            EFFORT_EXPLAINERS[EFFORT_LABELS[value] || 'Medium'],
            EFFORT_FOOTNOTE,
          ]
            .filter(Boolean)
            .join(' ') ||
          'Higher effort improves performance, but increases fatigue and injury risk.'
        : 'Choose how your team is organized on the field.';
  return <Control className={className} title={title || defaultTitle}><Select
    aria-label={label} inputId={`strategy-${label}`} classNamePrefix="strategy-select" isSearchable={false} isDisabled={disabled}
    value={options.find(option=>option.value===value)} options={options} onChange={option=>onChange(option.value)}
    formatOptionLabel={(option, {context})=>{
      const Icon=(label==='Mindset'?tacticIcons:effortIcons)[option.value];
      const eyebrow = label === 'Mindset' ? 'Tactic' : label;
      return (
        <div className="strategy-option">
          <Icon />
          <span className="strategy-option-copy">
            {context === 'value' && <small>{eyebrow}</small>}
            <span>{option.label}</span>
          </span>
        </div>
      );
    }}
  /></Control>;
}

function FormationStyleFilters({ styleFilter, onStyleFilter }) {
  return (
    <div
      className="style-filters"
      role="group"
      aria-label="Filter formations by style"
      onMouseDown={(event) => {
        // Keep the select menu open while toggling filters.
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {STYLE_FILTERS.map((style) => (
        <button
          key={style}
          type="button"
          aria-pressed={styleFilter === style}
          onClick={() =>
            onStyleFilter(styleFilter === style ? null : style)
          }
        >
          {style}
        </button>
      ))}
    </div>
  );
}

/** Short cycle for the formation menu: Wide → Central → Direct → Wide. */
function styleBeatsCycle(beats) {
  if (!beats || typeof beats !== 'object') return null;
  const start = ['Wide', 'Central', 'Direct'].find((style) => beats[style]);
  if (!start) return null;
  const second = beats[start];
  const third = second ? beats[second] : null;
  if (!second || !third || beats[third] !== start) {
    return Object.keys(beats)
      .filter((style) => beats[style])
      .map((style) => `${style} → ${beats[style]}`)
      .join(' · ');
  }
  return `${start} → ${second} → ${third} → ${start}`;
}

function FormationSelect({
  value,
  options,
  onChange,
  disabled,
  title,
  beatsLine,
}) {
  const [styleFilter, setStyleFilter] = useState(null);
  const filteredOptions = useMemo(() => {
    if (!styleFilter) return options;
    return options.filter((option) => option.style === styleFilter);
  }, [options, styleFilter]);

  const menuList = useMemo(
    () =>
      function FormationMenuList(props) {
        return (
          <components.MenuList {...props}>
            <FormationStyleFilters
              styleFilter={styleFilter}
              onStyleFilter={setStyleFilter}
            />
            {beatsLine ? (
              <p className="style-beats" title="Each style beats the next">
                {beatsLine}
              </p>
            ) : null}
            {props.children}
          </components.MenuList>
        );
      },
    [styleFilter, beatsLine]
  );

  const selected =
    options.find((option) => option.value === value) || null;

  return (
    <Control className="formation-section" title={title}>
      <Select
        aria-label="Formation"
        inputId="strategy-Formation"
        classNamePrefix="strategy-select"
        isSearchable={false}
        isDisabled={disabled}
        value={selected}
        options={filteredOptions}
        onChange={(option) => onChange(option.value)}
        onMenuClose={() => setStyleFilter(null)}
        components={{ MenuList: menuList }}
        noOptionsMessage={() =>
          styleFilter ? `No ${styleFilter} formations` : 'No formations'
        }
        formatOptionLabel={(option, { context }) => {
          const Icon = formations[option.value];
          return (
            <div className="strategy-option">
              <Icon />
              <span className="strategy-option-copy">
                {(context === 'value' || option.style) && (
                  <small>{option.style || 'Formation'}</small>
                )}
                <span>{option.value}</span>
              </span>
            </div>
          );
        }}
      />
    </Control>
  );
}

export default function StrategyToolbar({ children }) {
  const dispatch = useDispatch();
  const formation = useSelector(getCurrentStrategy);
  const { mindset, effort } = useSelector(getStrategyTactics);
  const loading = useSelector(isStrategyLoading);
  const formationStyles = useSelector(getFormationStyles);
  const matchRunning = useSelector(getCurrentMatchRunning);
  const teamInfo = useSelector(getMyCurrentTeam);
  const userTeamId = teamInfo?._id || teamInfo?.id;

  const formationOptions = Object.keys(STRATEGY_NAMES).map((value) => {
    const style = formationStyleOf(formationStyles, value);
    return {
      value,
      style: style || null,
      label: style ? `${value} · ${style}` : value,
    };
  });

  const effortLabel = EFFORT_LABELS[effort] || 'Medium';
  const effortTitle = [EFFORT_EXPLAINERS[effortLabel], EFFORT_FOOTNOTE]
    .filter(Boolean)
    .join(' ');

  const side = ownClubId(matchRunning, userTeamId);
  const myStyle =
    side === 'home'
      ? matchRunning?.home_style
      : side === 'away'
        ? matchRunning?.away_style
        : null;
  const theirStyle =
    side === 'home'
      ? matchRunning?.away_style
      : side === 'away'
        ? matchRunning?.home_style
        : null;
  const liveMatchup = matchupLabel(formationStyles, myStyle, theirStyle);
  const styleLabel = formationStyleOf(formationStyles, formation);
  const beatsLine = styleBeatsCycle(formationStyles?.beats);
  const formationTitle = [
    styleLabel ? `Your style: ${styleLabel}` : null,
    beatsLine ? `${beatsLine} (each beats the next)` : null,
    liveMatchup && myStyle && theirStyle
      ? `Live matchup: ${liveMatchup} (${myStyle} vs ${theirStyle})`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Toolbar>
      <FormationSelect
        disabled={loading}
        value={formation}
        options={formationOptions}
        title={formationTitle || undefined}
        beatsLine={beatsLine}
        onChange={(value) => {
          dispatch(teamPickUpStrategy(value));
          dispatch(setTacticData(`button-${STRATEGY_NAMES[value]}`));
          dispatch(setUnsavedChanges(true));
        }}
      />
      <StrategySelect
        label="Mindset"
        className="tactic-section"
        disabled={loading}
        value={mindset}
        options={['Defend', 'Neutral', 'Attack'].map((label, value) => ({
          label,
          value,
        }))}
        onChange={(value) => dispatch(setMindsetData(value))}
      />
      <StrategySelect
        label="Effort"
        disabled={loading}
        value={effort}
        options={EFFORT_LABELS.map((label, value) => ({
          label,
          value,
        }))}
        title={effortTitle}
        onChange={(value) => dispatch(setEffortData(value))}
      />
      {children}
    </Toolbar>
  );
}
